import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Inject, PLATFORM_ID, AfterViewInit } from "@angular/core";
import { SearchService } from "src/app/shared/services/search.service";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { filter, takeUntil, tap, take, distinctUntilChanged } from 'rxjs/operators';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { StoreService } from 'src/app/core/services/store.service';
import { SeoService } from "src/app/shared/services/seo.service";
import { AnalyticsService } from "src/app/shared/services/analytics.service";
import { CitiesService } from "src/app/shared/services/cities.service";
import { isNotEmpty } from "src/app/core/guards/type.guards";
import { ICategories, ICategory } from "src/app/core/models/categories";
import { EventSearchFilter, GraphResponse } from "src/app/core/models/models";
import { ICity } from "src/app/core/models/city";
import { IntersectionStatus } from "src/app/shared/observers/from-intersection-observer";
import { IEvent, IRepeatEvent } from "src/app/shared/interfaces";
import { LocalstorageService } from "src/app/core/services/local-storage.service";
import { CategoriesService } from "src/app/shared/services/categories.service";
import { SearchableSortDirection, SearchPlacesSortFields, SearchPlacesSortInput } from "src/app/core/models/entity";
import { Location } from '@angular/common';

@Component({
  selector: "ping-events-page",
  templateUrl: "./events-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["./events-page.component.scss"]
})
export class EventsPageComponent implements OnInit, AfterViewInit {
  public $events = new BehaviorSubject<GraphResponse<any>>(null);
  public $categories = new BehaviorSubject<ICategories[]>(null);
  public $cities = new BehaviorSubject<ICity[]>(null);
  // public $cities: Observable<ICity[]>;
  private $destroy = new Subject();
  public subcategories: any;
  public sortInput: SearchPlacesSortInput;
  public currentCity: ICity;

  public eventNotFound: boolean;

  public filters = {
    category: null,
    subCategory: null,
    orderBy: 'score',
    city: null,
    area: null
  }

  public searchInputs: EventSearchFilter = {
    city: null,
    area: null,
    categories: [],
    subCategories: [],
  };
  public loadingMore: boolean;

  constructor(private searchService: SearchService,
    private router: Router,
    private route: ActivatedRoute,
    private utils: UtilitiesService,
    private seoService: SeoService,
    private analyticsService: AnalyticsService,
    private localstorageService: LocalstorageService,
    private categoriesService: CategoriesService,
    private store: StoreService,
    private location: Location,
    private cd: ChangeDetectorRef,
    private cityService: CitiesService) { }

  ngOnInit(): void {
    if (window?.scroll) { window.scroll(0, 0); }
    if (this.route.snapshot.queryParams['not-found']){
      this.eventNotFound = true;
      this.cd.detectChanges();
    }

    const categoriesSnapshot = this.route.snapshot.data['categories'];
    if (categoriesSnapshot) {
      this.$categories.next(categoriesSnapshot);
    } else {
      this.categoriesService.fetchCategories();
    }
    this.store.object$('categories').pipe(takeUntil(this.$destroy), filter(isNotEmpty)).subscribe((cats: ICategories[]) => {
      this.$categories.next(cats);
    });

    const citiesSnapshot = this.route.snapshot.data['cities'];
    if (citiesSnapshot) {
      this.$cities.next(citiesSnapshot);
    } else {
      this.cityService.getCities();
    }
    this.store.object$('cities').pipe(takeUntil(this.$destroy), filter(isNotEmpty)).subscribe((cits: ICity[]) => {
      this.$cities.next(cits);
    });


    const events = this.route.snapshot.data['events'];
    this.$events.next(events);

    if (!this.store.getList('events-events') && !events) {
      this.search();
    }

    // now use the categories and cities on page load to populate the page using the url params
    combineLatest([
      this.$categories,
      this.$cities
      ])
      .pipe(filter(isNotEmpty),takeUntil(this.$destroy), distinctUntilChanged())
      .subscribe((data: any[]) => {

        if (!data || (data && (!data[0] || !data[1]))) { return; }
        const categories = data[0] || null;
        const cities = data[1] || null;

            // now change categories and cities based on urls

            this.route.params
              .pipe(takeUntil(this.$destroy))
              .subscribe((params: Params) => {

                // get city for search, default to london for map view to work
                let cityForSearch: string;
                if (params?.city) {
                  cityForSearch = this.utils.decodeRoute(params.city);
                } else if (this.store.getKey('city')) {
                  cityForSearch = this.store.getKey('city')
                } else if (typeof localStorage !== 'undefined') {
                  cityForSearch = localStorage.getItem('city') || 'London';
                } else {
                  cityForSearch = 'London';
                }
                this.searchInputs.city = cityForSearch;
                this.filters.city = cityForSearch;

                // get blogs for specific city on sidebar
                // this.getSidebarBlogs(this.filters.city);

                if (cities?.length) {
                  const cityIndex = cities.findIndex((c: ICity) => c.title === this.searchInputs.city);
                  if (cityIndex > -1) {
                    this.updateCity(cities[cityIndex]);
                  }
                }


                if (params?.category && categories?.length) {
                  const catParam = this.utils.decodeRoute(params.category);
                  const cat: ICategories = categories.find((c: ICategories) => c?.title === catParam);
                  this.filters.category = cat || null;
                  this.subcategories = this.filters?.category?.subCategories;
                } else {
                  this.filters.category = null;
                  this.searchInputs.categories = null;
                }

                if (params?.subCategory && this.filters?.category?.subCategories?.length) {
                  const catSubParam = this.utils.decodeRoute(params.subCategory);
                  const subCat: ICategory = this.filters?.category.subCategories.find((c: ICategory) => c?.title === catSubParam);
                  this.filters.subCategory = subCat?.title || null;
                } else {
                  this.filters.subCategory = null;
                  this.searchInputs.subCategories = null;
                }

                if (params?.area) {
                  const areaParam = this.utils.decodeRoute(params.area);
                  this.filters.area = areaParam;
                } else {
                  this.filters.area = null;
                  this.searchInputs.area = null;
                }

                this.cd.detectChanges();

                this.store.list$('events-events')
                  .pipe(
                    takeUntil(this.$destroy),
                    filter(isNotEmpty)
                  )
                  .subscribe((res: any) => {
                    if (res) {
                      this.$events.next(res);
                    }
                  })
                this.$events.pipe(
                  tap(() => {
                    // this.utils.stopSpinner();
                  }, takeUntil(this.$destroy))).subscribe(() => {
                    this.loadingMore = false;
                  });

                this.applySEO();
              })

          });
  }

  ngAfterViewInit(): void {
    // if (!isPlatformServer(this.platformId)) {
      this.utils.stopSpinner();
    // }
  }

  public updateCity(city?: ICity) {
    this.store.setKey('city', city?.title || null);
    if (city?.title !== this.searchInputs.city) {
      this.filters = {
        category: null,
        subCategory: null,
        orderBy: 'score',
        city: city?.title || null,
        area: null
      }
      this.currentCity = city || null;
      if (!city) {
        this.localstorageService.removeItem('city');
      } else {
        this.localstorageService.setItem('city', city?.title);
      }
      this.cd.detectChanges();
      this.search()

      // Update the url to allow 'Back' functionality
      if (city) {
        this.updateUrl('city', city.title);
      }
    }
  }


  public updateCategory(cat?: ICategories) {
    this.filters.category = cat || null;
    this.subcategories = cat?.subCategories || [];
    this.searchInputs.subCategories = [];
    this.updateUrl('category', cat?.title || null);
    this.search();
  }

  public updateSubCategory(cat?: ICategory) {
    this.filters.subCategory = cat || null;
    this.searchInputs.subCategories = [cat?.title];
    this.updateUrl('sub-category', cat?.title || null);
    this.search();
  }

  public search(nextToken?: any) {
    this.loadingMore = true;
    this.cd.detectChanges();
    if (!nextToken) {
      this.$events.next(null);
    }
    if (this.filters.category) {
      this.searchInputs.categories = [this.filters.category?.title]
      this.analyticsService.addCount(this.filters.category?.title, 'categories-count', 'CATEGORY', 'views', 'category', 1);
      if (this.filters.city) {
        this.analyticsService.addCount(`${this.filters.city}#${this.filters.category?.title}`, 'categories-count', 'CATEGORY', 'views', 'category', 1);
      }
    } else {
      this.searchInputs.categories = [];
    }

    if (this.filters.subCategory) {
      this.searchInputs.subCategories = [this.filters.subCategory];
      this.analyticsService.addCount(this.filters.subCategory, 'categories-count', 'CATEGORY', 'views', 'category', 1);
      if (this.filters.city) {
        this.analyticsService.addCount(`${this.filters.city}#${this.filters.subCategory}`, 'categories-count', 'CATEGORY', 'views', 'category', 1);
      }
    } else {
      this.searchInputs.subCategories = [];
    }

    this.searchInputs.city = this.filters.city || null;

    if (this.filters.area) {
      this.searchInputs.area = this.filters.area;
    } else {
      this.searchInputs.area = null;
    }

    if (this.filters.orderBy) {
      this.sortInput = {
        direction: SearchableSortDirection.desc,
        field: SearchPlacesSortFields[this.filters.orderBy]
      }

      if (this.filters.orderBy === 'price') {
        this.sortInput.direction = SearchableSortDirection.asc;
      }
    }

    this.searchService.searchEvents(this.searchInputs, nextToken, 10, 'events-events');
  }

  public onVisibilityChanged(status: IntersectionStatus, nextTokens?: string[]) {
    if ((nextTokens[0] || nextTokens[1]) && status === IntersectionStatus.Visible) {
      this.loadMore(nextTokens);
    }
  }

  public goToPage(event: IEvent & IRepeatEvent): void {
    const urlSuffix = event.username ? `event/${event.type}/${event.username}` : `type/${event.type}/${event.id}`;
    const eventRoute: any = `/events/${urlSuffix}`;
    window.open(eventRoute, "_blank");
  }

  private async searchForEvents(nextToken?: any): Promise<void> {
    this.searchService.searchEvents(this.searchInputs, nextToken, null, 'events-events');
  }

  public async loadMore(nextToken: string[]) {
    this.loadingMore = true;
    this.cd.detectChanges();
    if (nextToken[0] || nextToken[1]) {
      // set 'useNextTokens' to true so that it takes into account null next tokens (and doesn't fetch initial list)
    const res: any =  await this.searchService.searchEvents(this.searchInputs, nextToken, 9, 'events-events', true);
    }
  }

  private applySEO() {
    var area = this.filters.area ? this.filters.area + ', ' + this.filters.city :
      this.filters.city || null;
    var category = this.filters.subCategory ? this.filters.subCategory + ' ' + this.filters.category.title : this.filters.category?.title || null;
    this.filters.city || null;
    this.seoService.updateMeta(
      `Upcoming ${category || 'Events'} ${area ? 'in ' + area : 'in your area'} this weekend - Ping Culture`,
      `Check out the best upcoming ${category ? (category + ' events') : 'events'} ${area ? area : 'around you '} and book your tickets online at Ping Culture. Explore the dating events, new year events, and much more.`,
      this.router?.url || null,
      'https://ping-culture.com/assets/images/events.jpg'
    );
    this.seoService.createLinkForCanonicalURL('https://ping-culture.com/events');
  }

  public updateUrl(type: string, updateString?: string) {
    let url = this.location.path();
    let urlSplit = url.split('/');
    // Remove empty part of url
    urlSplit.shift();
    let urlJoin = '';

    if (type === 'city') {
      // see if already has city set
      if (urlSplit.indexOf("city") != -1) {
        urlSplit[2] = updateString;
      } else {
        // If not put city in correct place
        urlSplit.splice(1, 0, 'city')
        urlSplit.splice(2, 0, updateString)
      }

      // urlSplit.length = urlSplit.indexOf(updateString) + 1;
    }

    if (type === 'category') {
      // see if already has a category set
      if (urlSplit.indexOf("category") != -1) {
        if (updateString) {
          urlSplit[urlSplit.indexOf('category') + 1] = updateString;
        } else {
          // if has been set to ALL, remove from url
          urlSplit.splice(urlSplit.indexOf('category'), 2);
        }
      } else {
        // If not put category in correct place
        // check if city is set
        if (updateString) {
          if (urlSplit.indexOf('city') != -1) {
            urlSplit.splice(urlSplit.indexOf('city') + 2, 0, 'category');
            urlSplit.splice(urlSplit.indexOf('city') + 3, 0, updateString);
          } else {
            urlSplit.splice(1, 0, 'category')
            urlSplit.splice(2, 0, updateString)
          }
        }
      }

    }

    if (type === 'sub-category') {
      // see if already has a category set
      if (urlSplit.indexOf("sub-category") != -1) {
        if (updateString) {
          urlSplit[urlSplit.indexOf('sub-category') + 1] = updateString;
        } else {
          // if has been set to ALL, remove from url
          urlSplit.splice(urlSplit.indexOf('sub-category'), 2);
        }
      } else {
        // If not put subcategory in correct place after category
        urlSplit.splice(urlSplit.indexOf('category') + 2, 0, 'sub-category');
        urlSplit.splice(urlSplit.indexOf('category') + 3, 0, updateString);
      }
    }

    // Remove all values after as city or category or subcategories, as
    // cities have different categories and sub categories etc.
    if ((urlSplit.indexOf(updateString) != -1) && urlSplit.length > urlSplit.indexOf(updateString) + 1) {
      urlSplit.splice(urlSplit.indexOf(updateString) + 1, 10);
    }

    // this.location.replaceState(urlJoin);
    urlJoin = urlSplit.join('/');
    this.location.replaceState(urlJoin);
  }
}
